import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useLocale from './useLocale';

/**
 * Custom hook that wraps React Router navigate to automatically preserve locale parameter
 * in all navigation operations
 */
export default function useLanguageNavigate() {
  const navigate = useNavigate();
  const locale = useLocale();

  const languageNavigate = useCallback((to, options = {}) => {
    if (typeof to !== 'string') {
      return navigate(to, options);
    }
    
    // Don't add locale to external URLs
    if (to.startsWith('http://') || to.startsWith('https://')) {
      window.location.href = to;
      return;
    }

    // Don't add locale to anchor-only links
    if (to === '#' || to.startsWith('#')) {
      return navigate(to, options);
    }

    try {
      const urlObj = new URL(to, window.location.origin);
      
      // Preserve existing query params and add/update locale
      urlObj.searchParams.set('locale', locale);
      
      const newPath = urlObj.pathname + urlObj.search + urlObj.hash;
      return navigate(newPath, options);
    } catch (error) {
      // If URL parsing fails, just navigate normally
      return navigate(to, options);
    }
  }, [navigate, locale]);

  return languageNavigate;
}
